import { useState, useEffect, useCallback } from 'react';
import {
  Bug,
  Search,
  ArrowLeft,
  MessageSquare,
  Plus,
  X,
  ChevronDown,
  ChevronRight,
  Shield,
  Copy,
  Info,
} from 'lucide-react';
import {
  listBugs,
  getBugDetail,
  createBug,
  addBugComment,
  updateBug,
  markBugDuplicate,
} from '../api/client.js';
import type { BugListItem, BugDetail } from '../api/client.js';
import { useAuth } from '../hooks/useAuth.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SEVERITIES = ['critical', 'high', 'medium', 'low'];
const SEVERITY_LABELS: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};
const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-900/40 text-red-400',
  high: 'bg-orange-900/40 text-orange-400',
  medium: 'bg-yellow-900/40 text-yellow-400',
  low: 'bg-green-900/40 text-green-400',
};
const SEVERITY_BAR_COLORS: Record<string, string> = {
  critical: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-yellow-500',
  low: 'bg-green-500',
};
const SEVERITY_DESCRIPTIONS: Record<string, string> = {
  critical: 'System down or data loss — cannot use the application',
  high: 'Major feature broken — workaround may exist',
  medium: 'Feature partially broken — cosmetic or minor impact',
  low: 'Minor issue — cosmetic or edge case',
};

const STATUSES = [
  'new',
  'confirmed',
  'in-progress',
  'fixed',
  'closed',
  'cannot-reproduce',
  'wont-fix',
  'duplicate',
];
const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  confirmed: 'Confirmed',
  'in-progress': 'In Progress',
  fixed: 'Fixed',
  closed: 'Closed',
  'cannot-reproduce': 'Cannot Reproduce',
  'wont-fix': "Won't Fix",
  duplicate: 'Duplicate',
};
const STATUS_COLORS: Record<string, string> = {
  new: 'bg-gray-700 text-gray-300',
  confirmed: 'bg-blue-900/40 text-blue-400',
  'in-progress': 'bg-yellow-900/40 text-yellow-400',
  fixed: 'bg-green-900/40 text-green-400',
  closed: 'bg-gray-800 text-gray-500',
  'cannot-reproduce': 'bg-purple-900/40 text-purple-400',
  'wont-fix': 'bg-red-900/40 text-red-400',
  duplicate: 'bg-gray-800 text-gray-500',
};

const AFFECTED_AREAS = [
  'quotes',
  'quote-builder',
  'catalogs',
  'resellers',
  'pdf-export',
  'dashboard',
  'settings',
  'other',
];
const AREA_LABELS: Record<string, string> = {
  quotes: 'Quotes',
  'quote-builder': 'Quote Builder',
  catalogs: 'Catalogs',
  resellers: 'Resellers',
  'pdf-export': 'PDF Export',
  dashboard: 'Dashboard',
  settings: 'Settings',
  other: 'Other',
};
const AREA_COLORS: Record<string, string> = {
  quotes: 'bg-brand-600/20 text-brand-400',
  'quote-builder': 'bg-brand-600/20 text-brand-400',
  catalogs: 'bg-cyan-900/40 text-cyan-400',
  resellers: 'bg-violet-900/40 text-violet-400',
  'pdf-export': 'bg-pink-900/40 text-pink-400',
  dashboard: 'bg-amber-900/40 text-amber-400',
  settings: 'bg-teal-900/40 text-teal-400',
  other: 'bg-gray-700 text-gray-300',
};

const CLOSED_STATUSES = ['fixed', 'closed', 'cannot-reproduce', 'wont-fix', 'duplicate'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

// ---------------------------------------------------------------------------
// Submit Bug Modal
// ---------------------------------------------------------------------------

function SubmitBugModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (data: {
    title: string;
    stepsToReproduce: string;
    expectedBehavior: string;
    actualBehavior: string;
    severity: string;
    affectedArea: string;
  }) => Promise<void>;
}) {
  const [title, setTitle] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');
  const [expectedBehavior, setExpectedBehavior] = useState('');
  const [actualBehavior, setActualBehavior] = useState('');
  const [severity, setSeverity] = useState('medium');
  const [affectedArea, setAffectedArea] = useState(AFFECTED_AREAS[0]!);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSubmit = async () => {
    if (!title.trim() || !stepsToReproduce.trim() || !expectedBehavior.trim() || !actualBehavior.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await onSubmit({
        title: title.trim(),
        stepsToReproduce: stepsToReproduce.trim(),
        expectedBehavior: expectedBehavior.trim(),
        actualBehavior: actualBehavior.trim(),
        severity,
        affectedArea,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit bug report');
      setSubmitting(false);
    }
  };

  const isValid = title.trim() && stepsToReproduce.trim() && expectedBehavior.trim() && actualBehavior.trim();

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 py-8"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="mx-4 w-full max-w-2xl rounded-lg border border-gray-700 bg-gray-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-700 px-6 py-4">
          <h3 className="text-lg font-semibold">Report a Bug</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4 px-6 py-4">
          {error && (
            <div className="rounded-md bg-red-900/30 px-4 py-2 text-sm text-red-400">{error}</div>
          )}

          <div>
            <label className="mb-1 block text-sm text-gray-400">
              Title <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 200))}
              placeholder="Brief summary of the bug"
              className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
            />
            <div className="mt-1 text-right text-xs text-gray-600">{title.length}/200</div>
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-400">
              Steps to Reproduce <span className="text-red-400">*</span>
            </label>
            <textarea
              value={stepsToReproduce}
              onChange={(e) => setStepsToReproduce(e.target.value)}
              placeholder={"1. Go to ...\n2. Click on ...\n3. Observe ..."}
              rows={4}
              className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-400">
              Expected Behavior <span className="text-red-400">*</span>
            </label>
            <textarea
              value={expectedBehavior}
              onChange={(e) => setExpectedBehavior(e.target.value)}
              placeholder="What should happen..."
              rows={2}
              className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm text-gray-400">
              Actual Behavior <span className="text-red-400">*</span>
            </label>
            <textarea
              value={actualBehavior}
              onChange={(e) => setActualBehavior(e.target.value)}
              placeholder="What actually happens..."
              rows={2}
              className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1 block text-sm text-gray-400">
                Severity <span className="text-red-400">*</span>
              </label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none"
              >
                {SEVERITIES.map((s) => (
                  <option key={s} value={s}>
                    {SEVERITY_LABELS[s]}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-600">{SEVERITY_DESCRIPTIONS[severity]}</p>
            </div>

            <div>
              <label className="mb-1 block text-sm text-gray-400">
                Affected Area <span className="text-red-400">*</span>
              </label>
              <select
                value={affectedArea}
                onChange={(e) => setAffectedArea(e.target.value)}
                className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none"
              >
                {AFFECTED_AREAS.map((a) => (
                  <option key={a} value={a}>
                    {AREA_LABELS[a]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-700 px-6 py-4">
          <button
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !isValid}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Report Bug'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bug Card (list view)
// ---------------------------------------------------------------------------

function BugCard({ bug, onSelect }: { bug: BugListItem; onSelect: (id: string) => void }) {
  const isCritical = bug.severity === 'critical';

  return (
    <div
      className={`flex overflow-hidden rounded-lg border border-gray-800 transition-shadow hover:shadow-lg hover:shadow-black/20 ${
        isCritical ? 'bg-red-900/10' : 'bg-gray-900'
      }`}
    >
      {/* Severity color bar */}
      <div className={`w-1 flex-shrink-0 ${SEVERITY_BAR_COLORS[bug.severity] || 'bg-gray-600'}`} />

      {/* Content */}
      <div className="flex-1 cursor-pointer p-4" onClick={() => onSelect(bug.id)}>
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-medium text-gray-200 hover:text-white">{bug.title}</h3>
          <div className="flex flex-shrink-0 gap-2">
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${AREA_COLORS[bug.affectedArea] || AREA_COLORS.other}`}
            >
              {AREA_LABELS[bug.affectedArea] || bug.affectedArea}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[bug.status] || STATUS_COLORS.new}`}
            >
              {STATUS_LABELS[bug.status] || bug.status}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-medium ${SEVERITY_COLORS[bug.severity] || SEVERITY_COLORS.low}`}
            >
              {SEVERITY_LABELS[bug.severity] || bug.severity}
            </span>
          </div>
        </div>
        <p className="mt-1 line-clamp-1 text-sm text-gray-400">{bug.actualBehavior}</p>
        <div className="mt-2 flex items-center gap-3 text-xs text-gray-500">
          <span>Reported by {bug.authorName}</span>
          <span>{timeAgo(bug.createdAt)}</span>
          <span className="flex items-center gap-1">
            <MessageSquare size={12} /> {bug.commentCount}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bug Detail View
// ---------------------------------------------------------------------------

function BugDetailView({
  bugId,
  isAdmin,
  onBack,
}: {
  bugId: string;
  isAdmin: boolean;
  onBack: () => void;
}) {
  const [bug, setBug] = useState<BugDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Admin state
  const [adminStatus, setAdminStatus] = useState('');
  const [adminSeverity, setAdminSeverity] = useState('');
  const [adminArea, setAdminArea] = useState('');
  const [adminResponse, setAdminResponse] = useState('');
  const [saving, setSaving] = useState(false);

  // Duplicate state
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [dupSearch, setDupSearch] = useState('');
  const [dupResults, setDupResults] = useState<BugListItem[]>([]);
  const [marking, setMarking] = useState(false);

  // Duplicate bugs expand
  const [showDupBugs, setShowDupBugs] = useState(false);

  const loadBug = useCallback(async () => {
    try {
      const { bug: detail } = await getBugDetail(bugId);
      setBug(detail);
      setAdminStatus(detail.status);
      setAdminSeverity(detail.severity);
      setAdminArea(detail.affectedArea);
      setAdminResponse(detail.adminResponse || '');
    } catch {
      // handle error
    } finally {
      setLoading(false);
    }
  }, [bugId]);

  useEffect(() => {
    loadBug();
  }, [loadBug]);

  const handleAddComment = async () => {
    if (!commentText.trim()) return;
    setSubmittingComment(true);
    try {
      await addBugComment(bugId, commentText.trim());
      setCommentText('');
      await loadBug();
    } catch {
      // handle error
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleAdminUpdate = async () => {
    setSaving(true);
    try {
      const updates: Record<string, string> = {};
      if (adminStatus !== bug?.status) updates.status = adminStatus;
      if (adminSeverity !== bug?.severity) updates.severity = adminSeverity;
      if (adminArea !== bug?.affectedArea) updates.affectedArea = adminArea;
      if (adminResponse !== (bug?.adminResponse || '')) updates.adminResponse = adminResponse;

      if (Object.keys(updates).length > 0) {
        await updateBug(bugId, updates);
        await loadBug();
      }
    } catch {
      // handle error
    } finally {
      setSaving(false);
    }
  };

  const handleDupSearch = async () => {
    if (!dupSearch.trim()) return;
    try {
      const { bugs } = await listBugs({ search: dupSearch, includeClosed: true });
      setDupResults(bugs.filter((b) => b.id !== bugId && !b.duplicateOfId));
    } catch {
      setDupResults([]);
    }
  };

  const handleMarkDuplicate = async (targetId: string) => {
    if (!confirm('Mark this bug as a duplicate? Comments will be moved to the target bug.')) return;
    setMarking(true);
    try {
      await markBugDuplicate(bugId, targetId);
      setShowDuplicate(false);
      setDupSearch('');
      setDupResults([]);
      await loadBug();
    } catch {
      // handle error
    } finally {
      setMarking(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">Loading...</div>
    );
  }

  if (!bug) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        Bug report not found
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button + header */}
      <div>
        <button
          onClick={onBack}
          className="mb-4 flex items-center gap-1 text-sm text-gray-400 hover:text-gray-200"
        >
          <ArrowLeft size={16} /> Back to Bug Reports
        </button>

        <div className="flex items-start justify-between gap-4">
          <h2 className="text-2xl font-semibold">{bug.title}</h2>
          <div className="flex flex-shrink-0 gap-2">
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${SEVERITY_COLORS[bug.severity] || SEVERITY_COLORS.low}`}
            >
              {SEVERITY_LABELS[bug.severity] || bug.severity}
            </span>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_COLORS[bug.status] || STATUS_COLORS.new}`}
            >
              {STATUS_LABELS[bug.status] || bug.status}
            </span>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-medium ${AREA_COLORS[bug.affectedArea] || AREA_COLORS.other}`}
            >
              {AREA_LABELS[bug.affectedArea] || bug.affectedArea}
            </span>
          </div>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Reported by {bug.authorName} · {timeAgo(bug.createdAt)}
        </p>
      </div>

      {/* Bug details */}
      <div className="space-y-4">
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
          <h4 className="mb-2 text-sm font-medium text-gray-400">Steps to Reproduce</h4>
          <p className="whitespace-pre-wrap text-sm text-gray-300">{bug.stepsToReproduce}</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h4 className="mb-2 text-sm font-medium text-gray-400">Expected Behavior</h4>
            <p className="whitespace-pre-wrap text-sm text-gray-300">{bug.expectedBehavior}</p>
          </div>
          <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h4 className="mb-2 text-sm font-medium text-gray-400">Actual Behavior</h4>
            <p className="whitespace-pre-wrap text-sm text-gray-300">{bug.actualBehavior}</p>
          </div>
        </div>
      </div>

      {/* Admin response */}
      {bug.adminResponse && (
        <div className="rounded-lg border-l-4 border-brand-500 bg-gray-800/50 p-4">
          <div className="mb-1 flex items-center gap-2 text-xs font-medium text-brand-400">
            <Shield size={14} /> Admin Response
          </div>
          <p className="text-sm text-gray-300">{bug.adminResponse}</p>
        </div>
      )}

      {/* Duplicate bugs */}
      {bug.duplicateBugs.length > 0 && (
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-4">
          <button
            onClick={() => setShowDupBugs(!showDupBugs)}
            className="flex items-center gap-2 text-sm font-medium text-gray-400 hover:text-gray-200"
          >
            {showDupBugs ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <Copy size={14} />
            {bug.duplicateBugs.length} duplicate report{bug.duplicateBugs.length !== 1 ? 's' : ''}
          </button>
          {showDupBugs && (
            <ul className="mt-2 space-y-1 pl-8">
              {bug.duplicateBugs.map((d) => (
                <li key={d.id} className="text-sm text-gray-500">
                  {d.title}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Admin controls */}
      {isAdmin && (
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
          <h4 className="mb-4 flex items-center gap-2 text-sm font-medium text-gray-300">
            <Shield size={16} /> Admin Controls
          </h4>

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="mb-1 block text-xs text-gray-500">Status</label>
                <select
                  value={adminStatus}
                  onChange={(e) => setAdminStatus(e.target.value)}
                  className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {STATUS_LABELS[s]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-500">Severity</label>
                <select
                  value={adminSeverity}
                  onChange={(e) => setAdminSeverity(e.target.value)}
                  className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none"
                >
                  {SEVERITIES.map((s) => (
                    <option key={s} value={s}>
                      {SEVERITY_LABELS[s]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs text-gray-500">Affected Area</label>
                <select
                  value={adminArea}
                  onChange={(e) => setAdminArea(e.target.value)}
                  className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none"
                >
                  {AFFECTED_AREAS.map((a) => (
                    <option key={a} value={a}>
                      {AREA_LABELS[a]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs text-gray-500">Admin Response</label>
              <textarea
                value={adminResponse}
                onChange={(e) => setAdminResponse(e.target.value)}
                placeholder="Official response to this bug report..."
                rows={3}
                className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleAdminUpdate}
                disabled={saving}
                className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Update'}
              </button>

              <button
                onClick={() => setShowDuplicate(!showDuplicate)}
                className="flex items-center gap-1 rounded-md border border-gray-700 px-3 py-2 text-sm text-gray-300 hover:bg-gray-800"
              >
                <Copy size={14} /> Mark as Duplicate
              </button>
            </div>

            {/* Duplicate picker */}
            {showDuplicate && (
              <div className="rounded-md border border-gray-700 bg-gray-800 p-4">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={dupSearch}
                    onChange={(e) => setDupSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleDupSearch()}
                    placeholder="Search bug reports..."
                    className="flex-1 rounded-md border border-gray-600 bg-gray-900 px-3 py-1.5 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
                  />
                  <button
                    onClick={handleDupSearch}
                    className="rounded-md bg-gray-700 px-3 py-1.5 text-sm text-gray-200 hover:bg-gray-600"
                  >
                    Search
                  </button>
                </div>
                {dupResults.length > 0 && (
                  <ul className="mt-2 max-h-40 space-y-1 overflow-y-auto">
                    {dupResults.map((r) => (
                      <li
                        key={r.id}
                        className="flex items-center justify-between rounded-md px-3 py-2 text-sm hover:bg-gray-700"
                      >
                        <span className="text-gray-300">{r.title}</span>
                        <button
                          onClick={() => handleMarkDuplicate(r.id)}
                          disabled={marking}
                          className="text-xs text-brand-400 hover:text-brand-300 disabled:opacity-50"
                        >
                          Mark Duplicate
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Comments section */}
      <div className="rounded-lg border border-gray-800 bg-gray-900 p-6">
        <h4 className="mb-4 flex items-center gap-2 text-sm font-medium text-gray-300">
          <MessageSquare size={16} /> Discussion ({bug.comments.length} comments)
        </h4>

        {bug.comments.length > 0 ? (
          <div className="space-y-4">
            {bug.comments.map((comment) => (
              <div
                key={comment.id}
                className={`rounded-md p-3 ${
                  comment.isSystem
                    ? 'border border-gray-800 bg-gray-950'
                    : 'border border-gray-800 bg-gray-800/50'
                }`}
              >
                <div className="mb-1 flex items-center gap-2 text-xs text-gray-500">
                  {comment.isSystem ? <Info size={12} className="text-gray-600" /> : null}
                  <span
                    className={
                      comment.isSystem ? 'italic text-gray-600' : 'font-medium text-gray-400'
                    }
                  >
                    {comment.userName}
                  </span>
                  <span>{timeAgo(comment.createdAt)}</span>
                </div>
                <p
                  className={`text-sm ${
                    comment.isSystem ? 'italic text-gray-500' : 'text-gray-300'
                  }`}
                >
                  {comment.text}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="mb-4 text-sm text-gray-500">No comments yet.</p>
        )}

        {/* Add comment */}
        <div className="mt-4 border-t border-gray-800 pt-4">
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Add additional context, screenshots, or workarounds..."
            rows={3}
            className="w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
          />
          <div className="mt-2 flex justify-end">
            <button
              onClick={handleAddComment}
              disabled={submittingComment || !commentText.trim()}
              className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {submittingComment ? 'Posting...' : 'Post Comment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function BugReportsPage() {
  const { user } = useAuth();
  const isAdmin = !!user && ['owner', 'admin'].includes(user.role);

  // View state
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selectedBugId, setSelectedBugId] = useState<string | null>(null);

  // List state
  const [bugs, setBugs] = useState<BugListItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // Modal
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadBugs = useCallback(async () => {
    try {
      const isClosedStatus = filterStatus && CLOSED_STATUSES.includes(filterStatus);
      const { bugs: list } = await listBugs({
        severity: filterSeverity || undefined,
        status: filterStatus || undefined,
        affectedArea: filterArea || undefined,
        sort: sortBy,
        search: debouncedSearch || undefined,
        includeClosed: isClosedStatus || undefined,
      });
      setBugs(list);
    } catch {
      // handle error
    } finally {
      setLoading(false);
    }
  }, [filterSeverity, filterStatus, filterArea, sortBy, debouncedSearch]);

  useEffect(() => {
    loadBugs();
  }, [loadBugs]);

  const handleSelectBug = (id: string) => {
    setSelectedBugId(id);
    setView('detail');
  };

  const handleBack = () => {
    setView('list');
    setSelectedBugId(null);
    loadBugs();
  };

  const handleSubmitBug = async (data: {
    title: string;
    stepsToReproduce: string;
    expectedBehavior: string;
    actualBehavior: string;
    severity: string;
    affectedArea: string;
  }) => {
    await createBug(data);
    setShowSubmitModal(false);
    await loadBugs();
  };

  const hasActiveFilters = filterSeverity || filterStatus || filterArea || debouncedSearch;

  if (view === 'detail' && selectedBugId) {
    return (
      <div className="mx-auto max-w-7xl">
        <BugDetailView bugId={selectedBugId} isAdmin={isAdmin} onBack={handleBack} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Bug Reports</h2>
          <p className="mt-1 text-gray-400">Report and track bugs in OpenQuote Pro</p>
        </div>
        <button
          onClick={() => setShowSubmitModal(true)}
          className="flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
        >
          <Plus size={16} /> Report a Bug
        </button>
      </div>

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-800 bg-gray-900 px-4 py-3">
        <select
          value={filterSeverity}
          onChange={(e) => setFilterSeverity(e.target.value)}
          className="rounded-md border border-gray-700 bg-gray-800 px-2 py-1.5 text-xs text-gray-200 focus:border-brand-500 focus:outline-none"
        >
          <option value="">All Severities</option>
          {SEVERITIES.map((s) => (
            <option key={s} value={s}>
              {SEVERITY_LABELS[s]}
            </option>
          ))}
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-md border border-gray-700 bg-gray-800 px-2 py-1.5 text-xs text-gray-200 focus:border-brand-500 focus:outline-none"
        >
          <option value="">All Open</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s]}
            </option>
          ))}
        </select>

        <select
          value={filterArea}
          onChange={(e) => setFilterArea(e.target.value)}
          className="rounded-md border border-gray-700 bg-gray-800 px-2 py-1.5 text-xs text-gray-200 focus:border-brand-500 focus:outline-none"
        >
          <option value="">All Areas</option>
          {AFFECTED_AREAS.map((a) => (
            <option key={a} value={a}>
              {AREA_LABELS[a]}
            </option>
          ))}
        </select>

        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="rounded-md border border-gray-700 bg-gray-800 px-2 py-1.5 text-xs text-gray-200 focus:border-brand-500 focus:outline-none"
        >
          <option value="newest">Newest</option>
          <option value="severity">Severity</option>
          <option value="recently-updated">Recently Updated</option>
        </select>

        <div className="relative min-w-[160px] flex-1">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search bugs..."
            className="w-full rounded-md border border-gray-700 bg-gray-800 py-1.5 pl-7 pr-3 text-xs text-gray-200 placeholder-gray-500 focus:border-brand-500 focus:outline-none"
          />
        </div>

        {hasActiveFilters && (
          <button
            onClick={() => {
              setFilterSeverity('');
              setFilterStatus('');
              setFilterArea('');
              setSearchQuery('');
              setDebouncedSearch('');
            }}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        )}
      </div>

      {/* Bug list */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-500">
          Loading bug reports...
        </div>
      ) : bugs.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-gray-800 bg-gray-900 py-16">
          <Bug size={40} className="mb-3 text-gray-700" />
          <p className="text-gray-500">
            {hasActiveFilters
              ? 'No bugs match your filters.'
              : "No bugs reported. That's great news!"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {bugs.map((bug) => (
            <BugCard key={bug.id} bug={bug} onSelect={handleSelectBug} />
          ))}
        </div>
      )}

      {/* Submit modal */}
      {showSubmitModal && (
        <SubmitBugModal
          onClose={() => setShowSubmitModal(false)}
          onSubmit={handleSubmitBug}
        />
      )}
    </div>
  );
}
